/**
 * ActivityLog Component
 * Displays activity history in a chronological list
 * Supports both assignments and customers
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Clock,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Activity
} from 'lucide-react'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import {
  getAssignmentActivity,
  getCustomerActivity,
  getSubcontractorActivity,
  getActionDescription,
  getCustomerActionDescription,
  getSubcontractorActionDescription,
  getStatusLabel,
  type ActivityLogEntry
} from '../services/activityLog'

type EntityType = 'assignment' | 'customer' | 'subcontractor'

interface ActivityLogProps {
  assignmentId?: string
  customerId?: string
  subcontractorId?: string
  entityType?: EntityType
  isCollapsed?: boolean
  onToggleCollapse?: () => void
  /** When true, shows activities directly without collapsible header */
  inline?: boolean
}

export function ActivityLog({
  assignmentId,
  customerId,
  subcontractorId,
  entityType = 'assignment',
  isCollapsed = false,
  onToggleCollapse,
  inline = false
}: ActivityLogProps) {
  const entityId = entityType === 'customer'
    ? customerId
    : entityType === 'subcontractor'
      ? subcontractorId
      : assignmentId

  const { data: activities = [], isLoading, refetch } = useQuery({
    queryKey: ['activity-log', entityType, entityId],
    queryFn: () => {
      if (entityType === 'customer') return getCustomerActivity(entityId!)
      if (entityType === 'subcontractor') return getSubcontractorActivity(entityId!)
      return getAssignmentActivity(entityId!)
    },
    enabled: !!entityId
  })

  const getDescriptionForEntity = (entry: ActivityLogEntry) => {
    if (entityType === 'customer') return getCustomerActionDescription(entry)
    if (entityType === 'subcontractor') return getSubcontractorActionDescription(entry)
    return getActionDescription(entry)
  }

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'created':
        return '🆕'
      case 'status_changed':
        return '📊'
      case 'date_changed':
        return '📅'
      case 'subcontractor_changed':
        return '👤'
      case 'customer_changed':
        return '🏠'
      case 'articles_changed':
        return '📦'
      case 'assignment_added':
        return '📋'
      case 'assignment_removed':
        return '🗑️'
      case 'deleted':
        return '❌'
      case 'report_submitted':
        return '📝'
      case 'report_reopened':
        return '🔓'
      case 'updated':
        return '✏️'
      default:
        return '✏️'
    }
  }

  const formatValue = (value: string | null, actionType: string): string => {
    if (!value) return '-'
    
    // Format status values
    if (actionType === 'status_changed') {
      return getStatusLabel(value)
    }
    
    // Format date values
    if (actionType === 'date_changed' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
      try {
        return format(new Date(value), 'd MMMM yyyy', { locale: sv })
      } catch {
        return value
      }
    }
    
    return value
  }

  // Inline mode: show activities directly without wrapper
  if (inline) {
    return (
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
        {/* Simple header with title and refresh */}
        <div className="px-4 py-3 border-b border-nature-200 dark:border-nature-700 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Activity className="h-4 w-4 text-nature-500" />
            <h3 className="text-base font-semibold text-nature-900 dark:text-nature-100">
              Aktivitetslogg
            </h3>
            <span className="text-sm text-nature-500">
              ({activities.length})
            </span>
          </div>
          <button
            onClick={() => refetch()}
            className="p-1.5 hover:bg-nature-100 dark:hover:bg-nature-600 rounded"
            title="Uppdatera"
          >
            <RefreshCw className="h-4 w-4 text-nature-500" />
          </button>
        </div>

        {/* Content - always visible */}
        <div className="p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nature-600"></div>
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-8 text-nature-500 dark:text-nature-400">
              <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Ingen aktivitet loggad ännu</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activities.map((activity, index) => (
                <ActivityLogItem
                  key={activity.id}
                  activity={activity}
                  formatValue={formatValue}
                  getActionIcon={getActionIcon}
                  getDescription={getDescriptionForEntity}
                  isLast={index === activities.length - 1}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Collapsible mode (original)
  return (
    <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
      {/* Header */}
      <button
        onClick={onToggleCollapse}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <Activity className="h-5 w-5 text-nature-500" />
          <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
            Aktivitetslogg
          </h3>
          <span className="text-sm text-nature-500">
            ({activities.length} {activities.length === 1 ? 'händelse' : 'händelser'})
          </span>
        </div>
        <div className="flex items-center space-x-2">
          {!isCollapsed && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                refetch()
              }}
              className="p-1 hover:bg-nature-100 dark:hover:bg-nature-600 rounded"
              title="Uppdatera"
            >
              <RefreshCw className="h-4 w-4 text-nature-500" />
            </button>
          )}
          {isCollapsed ? (
            <ChevronDown className="h-5 w-5 text-nature-500" />
          ) : (
            <ChevronUp className="h-5 w-5 text-nature-500" />
          )}
        </div>
      </button>

      {/* Content */}
      {!isCollapsed && (
        <div className="px-4 pb-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nature-600"></div>
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-8 text-nature-500 dark:text-nature-400">
              <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Ingen aktivitet loggad ännu</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activities.map((activity, index) => (
                <ActivityLogItem
                  key={activity.id}
                  activity={activity}
                  formatValue={formatValue}
                  getActionIcon={getActionIcon}
                  getDescription={getDescriptionForEntity}
                  isLast={index === activities.length - 1}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface ActivityLogItemProps {
  activity: ActivityLogEntry
  formatValue: (value: string | null, actionType: string) => string
  getActionIcon: (actionType: string) => string
  getDescription: (entry: ActivityLogEntry) => string
  isLast: boolean
}

function ActivityLogItem({
  activity,
  formatValue,
  getActionIcon,
  getDescription,
  isLast
}: ActivityLogItemProps) {
  const [showDetails, setShowDetails] = useState(false)

  return (
    <div className={`relative pl-6 ${!isLast ? 'pb-3' : ''}`}>
      {/* Timeline line */}
      {!isLast && (
        <div className="absolute left-2 top-5 bottom-0 w-0.5 bg-nature-200 dark:bg-nature-600" />
      )}

      {/* Timeline dot */}
      <div className="absolute left-0 top-0.5 w-5 h-5 rounded-full bg-nature-100 dark:bg-nature-700 border-2 border-nature-300 dark:border-nature-500 flex items-center justify-center text-xs">
        {getActionIcon(activity.action_type)}
      </div>

      {/* Content */}
      <div className="bg-nature-50 dark:bg-nature-700/50 rounded-lg p-2">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm text-nature-900 dark:text-nature-100">
              <span className="font-medium">{activity.user_name || 'System'}</span>
              {' '}
              <span className="text-nature-600 dark:text-nature-400">
                {getDescription(activity)}
              </span>
            </p>
            
            {/* Show old → new value for changes */}
            {activity.old_value && activity.new_value && (
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="mt-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
              >
                {showDetails ? 'Dölj detaljer' : 'Visa detaljer'}
              </button>
            )}
            
            {showDetails && activity.old_value && activity.new_value && (
              <div className="mt-2 text-xs bg-[#faf9f7] dark:bg-nature-800 rounded p-2 border border-nature-200 dark:border-nature-600">
                <div className="flex items-center space-x-2">
                  <span className="text-red-500 line-through">
                    {formatValue(activity.old_value, activity.action_type)}
                  </span>
                  <span className="text-nature-400">→</span>
                  <span className="text-green-600 dark:text-green-400 font-medium">
                    {formatValue(activity.new_value, activity.action_type)}
                  </span>
                </div>
              </div>
            )}
          </div>
          
          {/* Timestamp */}
          <div className="flex items-center text-xs text-nature-500 dark:text-nature-400 ml-4">
            <Clock className="h-3 w-3 mr-1" />
            {format(new Date(activity.created_at), 'd MMM HH:mm', { locale: sv })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ActivityLog

